import { useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { documentsAreaMessages } from '../../messages';
import { Document, TabItem, DocumentType } from '../../model';
import { DocumentsAreaSections } from '../../model/documents/documents-area-sections';
import {
  communicationsDocumentsActions,
  documentsAreaCommonSliceActions,
  documentsListActions,
  getMarkDocumentAsRead,
  getNavigationMustReset,
  getSelectedDocumentsSection,
  getTotalUnreadCounts,
  offeringDocumentsActions,
} from '../../store/features/documents';
import {
  markDocumentAsRead,
  retrieveUnreadDocumentsCounts,
} from '../../store/features/documents/common/common-thunks';
import { downloadDocuments } from '../../store/features/documents/documents-list/documents-list-thunks';
import { useAppDispatch, useAppSelector } from '../redux';

export interface DocumentsAreaLogic {
  sections: TabItem<DocumentsAreaSections>[];
  selectedSection: DocumentsAreaSections;

  onSelectedSection: (selectedSection: DocumentsAreaSections) => void;
  handleDocumentsNavigation: () => void;
  onMarkDocumentAsRead: (
    document: Document,
    documentsAreaSection: DocumentsAreaSections,
    documentType?: DocumentType
  ) => void;
}

export const useDocumentsAreaLogic = (): DocumentsAreaLogic => {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();

  const selectedSection: DocumentsAreaSections = useAppSelector(
    getSelectedDocumentsSection
  );
  const totalUnreadCounts:any = useAppSelector(getTotalUnreadCounts);
  const navigationMustReset:any = useAppSelector(getNavigationMustReset);
  const markDocumentAsReadRequestStatus:any = useAppSelector(getMarkDocumentAsRead);
  const token: string = useAppSelector((state) => state.data?.user?.token);
  const userCode: string = useAppSelector(
    (state) => state.data?.user?.userCode
  );

  const sections = useMemo<TabItem<DocumentsAreaSections>[]>(() => {
    return [
      {
        type: 'DOCUMENTS',
        label: formatMessage(documentsAreaMessages.documentTab),
        count: totalUnreadCounts.DOCUMENTS,
      },
      {
        type: 'INFO_OFFER',
        label: formatMessage(documentsAreaMessages.informationOfferTab),
        count: totalUnreadCounts.INFO_OFFER,
      },
      {
        type: 'COMMUNICATIONS',
        label: formatMessage(documentsAreaMessages.communicationsTab),
        count: totalUnreadCounts.COMMUNICATIONS,
      },
    ];
  }, [formatMessage, totalUnreadCounts]);

  useEffect(() => {
    if (navigationMustReset) {
      dispatch(documentsAreaCommonSliceActions.resetWalletCommonState());
      dispatch(documentsListActions.reset());
      dispatch(offeringDocumentsActions.reset());
      dispatch(communicationsDocumentsActions.reset());
    }
    if (token !== undefined && userCode !== undefined) {
      dispatch(retrieveUnreadDocumentsCounts());
      dispatch(downloadDocuments());
    }

    return () => {
      if (navigationMustReset) {
        dispatch(documentsAreaCommonSliceActions.resetWalletCommonState());
        dispatch(documentsListActions.reset());
        dispatch(offeringDocumentsActions.reset());
        dispatch(communicationsDocumentsActions.reset());
      } else {
        dispatch(documentsAreaCommonSliceActions.setNavigationMustReset(true));
      }
    };
  }, [dispatch, navigationMustReset, token, userCode]);

  const onSelectedSection = useCallback(
    (sectionType: DocumentsAreaSections) => {
      dispatch(documentsAreaCommonSliceActions.setSelectedSection(sectionType));
    },
    [dispatch]
  );

  const handleDocumentsNavigation = useCallback(() => {
    dispatch(documentsAreaCommonSliceActions.setNavigationMustReset(false));
  }, [dispatch]);

  const onMarkDocumentAsRead = useCallback(
    (
      document: Document,
      documentsAreaSection: DocumentsAreaSections,
      documentType: DocumentType
    ) => {
      if (!document.flagIsRead) {
        dispatch(
          markDocumentAsRead({
            document,
            documentsAreaSection,
            documentType,
          })
        );
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (markDocumentAsReadRequestStatus === 'succeeded') {
      dispatch(documentsAreaCommonSliceActions.clearMarkDocumentAsRead());
    }
  }, [markDocumentAsReadRequestStatus, selectedSection, dispatch]);

  return {
    sections,
    selectedSection,
    onSelectedSection,
    handleDocumentsNavigation,
    onMarkDocumentAsRead,
  };
};
